// The left nav's destinations, as data.
//
// This lived as ~90 lines of conditional array spreads inside _LeftNav.jsx,
// which is how three role gates drifted apart under a comment claiming they
// agreed. As data, each gate is one testable predicate.
//
// Each item's `id` is stable vocabulary: pins are stored by id, never by
// path, so pinning survives a route being reshaped.

import { canManage } from "../../utils/roles";

// Matches the check ReviewPage and PlanningPage apply before they render: a
// role compared case-insensitively, PLUS a superuser with no employee role.
// Stricter here than there would hide a link to a route that still serves it.
const isManager = (user) =>
  Boolean(
    user &&
      (user.is_superuser ||
        ["MANAGER", "ADMIN"].includes(String(user.role).toUpperCase()))
  );

// Matches the original _LeftNav.jsx exactly: case-sensitive comparison.
// EmployeesPage has no gate of its own, so this nav link is the gate. As such,
// this package does not widen who can see it — that would be a security
// decision with its own ticket. A user whose role is "staff" (lowercase) is
// a data inconsistency or a platform change, not this package's call.
const isStaffRole = (user) =>
  ["MANAGER", "ADMIN", "STAFF"].includes(user?.role);

export const SECTIONS = [
  { key: "work", label: "Work" },
  { key: "time", label: "Time" },
  { key: "billing", label: "Billing" },
  { key: "clients", label: "Clients" },
  { key: "firm", label: "Firm" },
];

// `visibleTo` is the gate; `resolve` (optional) adapts an item to the user.
export const NAV_ITEMS = [
  { id: "workflow", path: "/workflow", icon: "🗂️", label: "Workflow",
    section: "work" },
  { id: "tasks", path: "/tasks", icon: "📋", label: "Tasks", section: "work" },
  { id: "planning", path: "/planning", icon: "🗓️", label: "Planning",
    section: "work", visibleTo: isManager },

  { id: "time-entry", path: "/time", icon: "⏰", label: "Time Entry",
    section: "time" },
  // Per-employee flag, not a role -- a manager who should not approve time
  // does not see this.
  { id: "time-approval", path: "/time-approval", icon: "✅",
    label: "Time Approval", section: "time",
    visibleTo: (user) => Boolean(user?.can_approve_time) },
  // One route, two meanings. For staff it is their own record and belongs
  // with Time; for a manager it is the staff list and belongs with Firm.
  { id: "employees", path: "/employees", icon: "👨‍💼", label: "Employees",
    section: "firm", visibleTo: isStaffRole,
    resolve: (item, user) =>
      String(user?.role).toUpperCase() === "STAFF"
        ? { ...item, icon: "👤", label: "My Time", section: "time" }
        : item },

  { id: "create-invoice", path: "/invoices/create", icon: "➕",
    label: "Create Invoice", section: "billing" },
  { id: "invoices", path: "/invoices", icon: "📄", label: "Invoices",
    section: "billing" },

  { id: "clients", path: "/clients", icon: "👥", label: "Clients",
    section: "clients" },
  { id: "archived", path: "/archived", icon: "📦", label: "Archived",
    section: "clients" },

  // Deliberately unchanged: HrDashboardPage has no gate of its own and nor do
  // the /dashboard/ endpoints it reads, so this link is the only thing in
  // front of them. Widening it is a security decision with its own ticket --
  // see the spec. Narrowing it is not this package's call either.
  { id: "hr-dashboard", path: "/hr-dashboard", icon: "📊",
    label: "HR Dashboard", section: "firm",
    visibleTo: (user) => ["MANAGER", "ADMIN"].includes(user?.role) },
  { id: "review", path: "/review", icon: "📈", label: "Review",
    section: "firm", visibleTo: isManager },
  { id: "explore", path: "/explore", icon: "🔍", label: "Data Explorer",
    section: "firm" },
  // canManage is the rule TimeCodesPage applies to its own actions, and
  // utils/roles.js exists so these gates cannot drift.
  { id: "time-codes", path: "/time-codes", icon: "⚙️",
    label: "Status & Time Codes", section: "firm", visibleTo: canManage },
  // Everyone: the point is that anyone can say what should change.
  { id: "improvements", path: "/improvements", icon: "💡",
    label: "App Improvements", section: "firm" },
];

export function visibleNavItems(user) {
  return NAV_ITEMS
    .filter((item) => (item.visibleTo ? item.visibleTo(user) : true))
    .map((item) => (item.resolve ? item.resolve(item, user) : item));
}
